import React, { Children, createRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Slide, Fade } from '@wds/motion';
import { safeRest, uniqueId } from '@wds/utils';
import { useTheme } from 'react-jss';

import { SIZES } from '@wds/helpers';
import { CloseButton, Overlay } from '@wds/internal-components';

import ModalBody from './ModalBody';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';

import useStyles from './useStyles';

export const MODAL_SIZES = ['xl', ...SIZES];

const Modal = ({
  children,
  closeButtonLabel,
  dismissible,
  onClose,
  show,
  size,
  title,
  ...rest
}) => {
  const modalBodyRef = createRef();
  const theme = useTheme();
  const {
    wrenchModal,
    wrenchModalBody,
    wrenchModalContainer,
    wrenchModalOverlay,
  } = useStyles({
    size,
    theme,
  });

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.focus();
    }

    if (typeof document !== 'undefined') {
      const parent = document.getElementsByTagName('html')[0];
      if (parent) {
        if (show) {
          parent.style.overflow = 'hidden';
        } else {
          parent.style.overflow = 'auto';
        }
      }
    }
  }, [modalBodyRef, show]);

  const titleId = useMemo(() => uniqueId('modal-title-'));
  const bodyId = useMemo(() => uniqueId('modal-body-'));

  const handleClick = () => {
    if (dismissible) {
      if (onClose) onClose();
    }
  };

  /**
   * ModalHeader exists show aria-labelledby
   * @type {boolean}
   */
  const hasModalHeader = Children.toArray(children).some(
    ({ type }) => type.displayName === 'ModalHeader'
  );

  return (
    <>
      <Fade in={show}>
        <div>
          <Overlay onClick={handleClick} className={wrenchModalOverlay} />
        </div>
      </Fade>

      <Slide in={show} direction="down" distance="near" speed="fast">
        <div>
          <div className={wrenchModal} {...safeRest(rest)}>
            <div
              className={wrenchModalContainer}
              aria-label={title}
              aria-labelledby={hasModalHeader ? titleId : undefined}
              aria-describedby={bodyId}
              role="dialog"
              aria-modal="true"
              tabIndex="-1"
            >
              <If condition={dismissible}>
                <CloseButton
                  variant="dark"
                  onClick={handleClick}
                  label={closeButtonLabel}
                />
              </If>
              {Children.map(children, (child) => {
                if (!React.isValidElement) {
                  return null;
                }

                const {
                  type: { displayName },
                } = child;

                const childProps = {
                  ModalHeader: { ...child.props, id: titleId },
                  ModalBody: {
                    ...child.props,
                    className: wrenchModalBody,
                    id: bodyId,
                    handleClick,
                    ref: modalBodyRef,
                  },
                }[displayName] || {
                  ...child.props,
                };

                return React.cloneElement(child, { ...childProps });
              })}
            </div>
          </div>
        </div>
      </Slide>
    </>
  );
};

Modal.propTypes = {
  /**
   * Composed of Modal.Header, Modal.Body, Modal.Footer
   */
  children: PropTypes.node.isRequired,
  /**
   * Label for close button on top right of Modal
   */
  closeButtonLabel: PropTypes.node,
  /**
   * If false, X button on top right of Modal will be hidden
   */
  dismissible: PropTypes.bool,
  /**
   * onClose handler
   * function (event: object) => void
   */
  onClose: PropTypes.func,
  /**
   * If true, Modal will be displayed
   */
  show: PropTypes.bool,
  /**
   * Modal size variants
   */
  size: PropTypes.oneOf(MODAL_SIZES),
  /**
   * Define aria title for Modal
   */
  title: PropTypes.string,
};

Modal.defaultProps = {
  closeButtonLabel: 'Close Modal',
  dismissible: true,
  onClose: undefined,
  show: false,
  size: 'sm',
  title: undefined,
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Header = ModalHeader;

export default Modal;
